import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useLocation } from "wouter";
import {
  Shield, ChevronLeft, ScanFace, Eye, Navigation,
  MonitorDot, Crosshair, Settings, BrainCircuit, FileBarChart
} from "lucide-react";
import { ConnectionStatusBadge } from "@/components/ConnectionStatusBadge";

interface CommandNavProps {
  /** Current page title shown next to logo */
  pageTitle?: string;
  /** Extra content on the right side (buttons, status, etc) */
  rightContent?: React.ReactNode;
  /** Status indicator (connected/disconnected) */
  statusIndicator?: { connected: boolean; label?: string };
  /** Show back button to home */
  showBack?: boolean;
}

export default function CommandNav({ pageTitle, rightContent, statusIndicator, showBack = true }: CommandNavProps) {
  const [location, navigate] = useLocation();

  const navItems = [
    { label: "Dashboard", href: "/dashboard", icon: MonitorDot },
    { label: "Banco Facial", href: "/persons", icon: ScanFace },
    { label: "Eventos", href: "/events", icon: Eye },
    { label: "Agentes", href: "/agents", icon: Crosshair },
    { label: "Regras", href: "/routing", icon: Navigation },
    { label: "Relatórios", href: "/reports", icon: FileBarChart },
  ];

  return (
    <header
      className="h-14 border-b flex items-center justify-between px-4 shrink-0"
      style={{
        background: "rgba(5,5,16,0.92)",
        backdropFilter: "blur(20px)",
        borderColor: "rgba(139,92,246,0.1)",
      }}
    >
      {/* Left: Logo + Page title */}
      <div className="flex items-center gap-3">
        {showBack && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/")}
            className="h-8 w-8 text-white/40 hover:text-white/80 hover:bg-white/[0.05]"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
        )}
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 40%, #ec4899 100%)" }}
        >
          <Shield className="w-4 h-4 text-white" />
        </div>
        <div className="flex flex-col leading-none">
          <span className="font-bold text-sm tracking-tight text-white" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            ELLA <span className="text-purple-400/50 text-xs font-normal ml-0.5">by GAP</span>
          </span>
          {pageTitle && (
            <span className="text-[10px] text-purple-300/35 tracking-[0.15em] uppercase font-medium">
              {pageTitle}
            </span>
          )}
        </div>

        {statusIndicator && (
          <>
            <Separator orientation="vertical" className="h-5 mx-1 bg-white/[0.06]" />
            <div className="flex items-center gap-1.5">
              <div className={`w-2 h-2 rounded-full ${statusIndicator.connected ? "bg-green-400" : "bg-red-400"} animate-pulse`} />
              <span className="text-xs text-white/35">
                {statusIndicator.label || (statusIndicator.connected ? "Conectado" : "Desconectado")}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Center: Nav items */}
      <div className="hidden md:flex items-center gap-0.5">
        {navItems.map((item) => {
          const isActive = location === item.href || location.startsWith(item.href + "/");
          return (
            <button
              key={item.label}
              onClick={() => navigate(item.href)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-200 ${
                isActive
                  ? "text-purple-300 bg-purple-500/10"
                  : "text-white/35 hover:text-white/70 hover:bg-white/[0.03]"
              }`}
            >
              <item.icon className="w-3.5 h-3.5" />
              {item.label}
            </button>
          );
        })}
      </div>

      {/* Right: Connection status + Custom content */}
      <div className="flex items-center gap-2">
        <ConnectionStatusBadge />
        {rightContent}
      </div>
    </header>
  );
}
